import { isValueNotEmpty } from '@awp/helpers/utilsReact';
import format from 'date-fns/format';
import { ReticleService } from '@graphs/Common/ReticleService';

/**
 * Creates or retrieves an existing tooltip DOM element associated with a given chart.
 *
 * @param {Chart} chart - The Chart.js instance where the tooltip is associated.
 * @returns {HTMLElement} The tooltip element.
 */
const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.classList.add('custom-tooltip');
    tooltipEl.style.zIndex = '9999';

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

/**
 * Determines whether to show a tooltip item based on various data flags.
 *
 * @param {object} itemData - The data object for the item.
 * @param {string} itemType - The type of the item (e.g., 'TemperatureProfile').
 * @param {boolean} showInvalidData - Flag to show invalid data.
 * @returns {boolean} True if the item should be shown, false otherwise.
 */
const shouldShowItem = (itemData, itemType, showInvalidData = false) => {
  if (!itemData.isValid && itemData.displayY === null && !(itemData.isMissing && showInvalidData)) return false;
  if (itemData.isNan && !showInvalidData && itemType != 'TemperatureProfile') return false;

  return true;
};

/**
 * Custom tooltip handler for Chart.js charts.
 * This handler configures the tooltip content, visibility, and styles based on chart data.
 *
 * @param {object} context - The context object with information about the tooltip and chart state.
 */
export const externalTooltipHandler = (context, setLastHoveredXValue) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  //frost probe has added a custom option element for tt handling of title
  const isFrostProbe = chart.config.options.frostProbe || false;
  const showInvalidData = chart.config.options.showInvalidData || false;
  const groupByWeatherStation = chart.config.options.groupByWeatherStation || false;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  let maxLength = 0;

  if (tooltip.body && chart._active[0]) {
    //use the first active element to get the timestamp, should either be a line or temperature element
    const timestamp = chart._active[0].customData.data.x;

    //build the tooltip title
    tooltip.title = tooltip.dataPoints.map((l) => l.label);

    //const title = isFrostProbe ? ['Depth: ' + chart._active[0].element.$context.parsed.y + 'cm'] : tooltip.title[0] || [];
    const title = isFrostProbe
      ? 'Depth: ' + chart._active[0].element.$context.parsed.y + 'cm'
      : format(new Date(timestamp), 'MMM d, yyyy, H:mm:ss') || '';
    const tableHead = document.createElement('thead');

    const tr = document.createElement('tr');
    tr.style.borderWidth = 0;

    const th = document.createElement('th');
    th.style.borderWidth = 0;
    const text = document.createTextNode(title);

    th.appendChild(text);
    tr.appendChild(th);
    tableHead.appendChild(tr);

    if (title.length > maxLength) maxLength = title.length;

    //build the tooltip body
    const tableBody = document.createElement('tbody');

    let tooltipItems = chart._active;

    //this is where we want to branch the code based on
    // the groupByWeatherStation flag
    if (groupByWeatherStation) {
      tooltipItems = chart._active.reduce((a, o) => {
        const wxId = o.customData.weatherStationId;
        if (!a[wxId]) {
          a[wxId] = [];
        }
        a[wxId].push(o);
        return a;
      }, {});

      Object.entries(tooltipItems).forEach(([weatherStationId, items]) => {
        const weatherStationText = items[0].customData.weatherStationText;
        const stationTD = document.createElement('td');
        stationTD.style.borderWidth = 0;
        stationTD.style.paddingLeft = '0.3rem';
        stationTD.style.fontStyle = 'italic';
        stationTD.textContent = weatherStationText;

        const stationTR = document.createElement('tr');
        stationTR.style.backgroundColor = 'inherit';
        stationTR.style.borderWidth = 0;

        stationTR.appendChild(stationTD);
        tableBody.appendChild(stationTR);
        items
          .sort((a, b) => {
            const datasetComparison = a.customData.datasetName.localeCompare(b.customData.datasetName);
            if (datasetComparison !== 0) {
              return datasetComparison;
            }
            return a.customData.groupName.localeCompare(b.customData.groupName);
          })
          .forEach((item) => {
            const { td, textLength } = generateTooltipItemTD(item, showInvalidData, maxLength);
            if (textLength > maxLength) maxLength = textLength + 1;

            td.style.paddingLeft = '0.6rem';
            const tr = document.createElement('tr');
            tr.style.backgroundColor = 'inherit';
            tr.style.borderWidth = 0;
            tr.appendChild(td);
            tableBody.appendChild(tr);
          });
      });
    } else {
      tooltipItems.forEach((item) => {
        const { td, textLength } = generateTooltipItemTD(item, showInvalidData, maxLength);
        if (textLength > maxLength) maxLength = textLength + 1;

        const tr = document.createElement('tr');
        tr.style.backgroundColor = 'inherit';
        tr.style.borderWidth = 0;
        tr.appendChild(td);
        tableBody.appendChild(tr);
      });
    }

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY, offsetWidth: offsetWidth } = chart.canvas;
  const centerPoint = offsetWidth / 2;

  let tooltipLeft = positionX + tooltip.caretX;
  let tooltipWidth = maxLength * 8;

  tooltipWidth = tooltipWidth > centerPoint ? centerPoint : tooltipWidth;
  tooltipLeft = tooltipLeft > centerPoint ? tooltipLeft - tooltipWidth : tooltipLeft;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = tooltipLeft + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
  tooltipEl.style.width = tooltipWidth + 'px';

  // Save Reticle Value
  ReticleService.onAxisHover(tooltip, chart, setLastHoveredXValue);
};

/**
 * Generates an HTML table data cell element for a tooltip item.
 *
 * @param {object} item - The tooltip item.
 * @param {boolean} showInvalidData - Flag indicating whether invalid data should be shown.
 * @param {number} maxLength - The maximum text length used for width calculation.
 * @returns {object} An object containing the 'td' HTML element and text length.
 */
const generateTooltipItemTD = (item, showInvalidData, maxLength) => {
  const itemElement = item.customData;
  const itemData = itemElement.data;

  const td = document.createElement('td');
  td.style.borderWidth = 0;

  //work-around for WEAV-7384 & WEAV-10200
  if (!shouldShowItem(itemData, itemElement.type, showInvalidData)) return { td, maxLength };

  const span = document.createElement('span');
  span.style.background = itemElement.legendColor;
  span.style.borderColor = itemElement.legendColor;
  span.style.borderWidth = '2px';
  span.style.borderRadius = '50%';
  span.style.marginRight = '10px';
  span.style.height = '10px';
  span.style.width = '10px';
  span.style.display = 'inline-block';

  let displayedMeaning = '';

  //if yAxisList not null, always transform obs value to display order with displayedMeaning regardless transform
  if (isValueNotEmpty(itemElement.yAxisList)) {
    if (isValueNotEmpty(itemElement.yAxisList[itemData.y])) {
      displayedMeaning = ' ' + itemElement.yAxisList[itemData.y];
    }
  }

  let groupName = itemElement.groupName ? ` (${itemElement.groupName})` : '';
  if (itemElement?.sensorType?.Name) {
    groupName = ` (${itemElement.sensorType.Name})`;
  }

  const weatherStationNumber = itemElement.weatherStationInfo?.Number
    ? ` (${itemElement.weatherStationInfo.Number})`
    : '';

  let displayText = `${itemElement.datasetName}${groupName}${weatherStationNumber}: `;
  if (itemElement.type === 'TemperatureProfile') {
    if (itemData.isMissing) {
      displayText += 'Missing';
    } else if (itemData.isNan) {
      displayText += 'NaN ';
    } else if (itemData.isSpecial) {
      displayText += '-6999 ';
    } else {
      displayText += `${itemData.v}${itemElement.unitLabel}`;
    }
    displayText += ` @ ${itemData.y}cm` + '\xa0';
  } else {
    let displayValue = `${itemData.displayY}${itemElement.unitLabel}${displayedMeaning}`;

    displayValue = itemData.isMissing && showInvalidData ? 'Missing' : displayValue;
    displayValue = itemData.isNan && showInvalidData ? 'NaN ' : displayValue;
    displayText += `${displayValue}` + '\xa0';
  }

  const text = document.createTextNode(displayText);

  td.appendChild(span);
  td.appendChild(text);
  if (!itemData.isValid) {
    const invSpan = document.createElement('span');
    invSpan.classList.add('badge', 'badge-danger');
    invSpan.appendChild(document.createTextNode('!'));
    td.appendChild(invSpan);
  }

  return { td, textLength: text.length };
};
